import { Field } from '@/components/Field';
import { NumberInput } from '@/components/NumberInput';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AcquisitionModel } from '@/domain/types';
import { useRequiredAcquisition } from '@/hooks/useRequiredAcquisition';
import { formatNumber } from '@/lib/format';
import { useSimulatorState } from '@/store/useSimulatorState';

const MODEL_LABELS: Record<AcquisitionModel, string> = {
  's-curve': 'Montée lente (courbe en S)',
  'linear-ramp': 'Rampe linéaire',
  constant: 'Constant',
};

const MODEL_HELP: Record<AcquisitionModel, string> = {
  's-curve': 'Acquisition difficile au début, puis accélération jusqu’au rythme de croisière.',
  'linear-ramp': 'Croissance régulière du rythme jusqu’au plateau.',
  constant: 'Rythme fixe dès le premier mois.',
};

export function AcquisitionPanel() {
  const { acquisition, updateAcquisition } = useSimulatorState();
  const required = useRequiredAcquisition();
  const current = acquisition.newClientsPerMonth;
  const model = acquisition.model ?? 'constant';
  const rampUpMonths = acquisition.rampUpMonths ?? 6;
  const discrete = acquisition.discrete === true;
  const salesCycleMonths = acquisition.salesCycleMonths ?? 12;
  const winRatePercent = Math.round((acquisition.winRate ?? 0.3) * 100);
  const presalesCostPerDeal = acquisition.presalesCostPerDeal ?? 0;
  const roiReached = required !== null && current >= required;
  const headcountRampEnabled = useSimulatorState((s) => s.team.headcountRamp?.enabled === true);

  return (
    <div className="flex flex-col gap-4 py-2">
      <Field id="acquisitionModel" label="Modèle d’acquisition">
        <Select
          value={model}
          onValueChange={(v) => updateAcquisition({ model: v as AcquisitionModel })}
        >
          <SelectTrigger id="acquisitionModel" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="s-curve">{MODEL_LABELS['s-curve']}</SelectItem>
            <SelectItem value="linear-ramp">{MODEL_LABELS['linear-ramp']}</SelectItem>
            <SelectItem value="constant">{MODEL_LABELS.constant}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <p className="text-xs text-muted-foreground">{MODEL_HELP[model]}</p>

      <Field
        id="newClientsPerMonth"
        label={
          model === 'constant' ? 'Nouveaux clients / mois' : 'Rythme de croisière (clients/mois)'
        }
      >
        <NumberInput
          id="newClientsPerMonth"
          value={current}
          onChange={(v) => updateAcquisition({ newClientsPerMonth: v })}
          min={0}
          decimals={2}
        />
      </Field>
      <p className="text-xs text-muted-foreground">
        {discrete
          ? 'Un rythme fractionnaire est permis : 0,5 signifie une signature tous les deux mois en moyenne.'
          : 'Un rythme fractionnaire est permis (ex. 0,5 client/mois) : les clients s’accumulent en continu.'}
      </p>

      {model !== 'constant' && (
        <Field
          id="rampUpMonths"
          label="Durée d’atteinte du plateau (mois)"
          tooltip="Nombre de mois nécessaires pour atteindre le rythme de croisière."
        >
          <NumberInput
            id="rampUpMonths"
            value={rampUpMonths}
            onChange={(v) => updateAcquisition({ rampUpMonths: Math.round(v) })}
            min={0}
            max={60}
            decimals={0}
          />
        </Field>
      )}

      {/* Mode événementiel B2G — deals entiers, cycle de vente, coût d'avant-vente */}
      <div className="mt-2 flex flex-col gap-2 rounded-md border border-dashed p-3">
        <label
          htmlFor="acquisitionDiscrete"
          className="flex cursor-pointer items-center gap-2 text-sm"
        >
          <Checkbox
            id="acquisitionDiscrete"
            checked={discrete}
            onCheckedChange={(checked) => {
              if (checked) {
                updateAcquisition({
                  discrete: true,
                  salesCycleMonths: acquisition.salesCycleMonths ?? 12,
                  winRate: acquisition.winRate ?? 0.3,
                  presalesCostPerDeal: acquisition.presalesCostPerDeal ?? 0,
                });
              } else {
                updateAcquisition({ discrete: undefined });
              }
            }}
          />
          <span>Deals entiers avec cycle de vente (vente B2G)</span>
        </label>
        <p className="text-xs text-muted-foreground">
          Les clients sont signés par unités entières (le rythme devient un rythme moyen de
          signatures) et la facturation ne démarre qu&apos;après le cycle de vente. Le churn retire
          également des clients entiers.
        </p>
        {discrete && (
          <>
            <Field
              id="salesCycleMonths"
              label="Cycle de vente (mois)"
              tooltip="Délai entre la signature du deal et la première facturation (appel d'offres, homologation, notification)."
            >
              <NumberInput
                id="salesCycleMonths"
                value={salesCycleMonths}
                onChange={(v) => updateAcquisition({ salesCycleMonths: Math.round(v) })}
                min={0}
                max={60}
                decimals={0}
              />
            </Field>
            <Field
              id="winRate"
              label="Taux de win des dossiers (%)"
              tooltip="Part des dossiers d'avant-vente aboutissant à une signature. Les dossiers perdus coûtent aussi."
            >
              <NumberInput
                id="winRate"
                value={winRatePercent}
                onChange={(v) =>
                  updateAcquisition({ winRate: Math.min(Math.max(Math.round(v), 1), 100) / 100 })
                }
                min={1}
                max={100}
                decimals={0}
              />
            </Field>
            <Field
              id="presalesCostPerDeal"
              label="Coût d’avant-vente par dossier (€)"
              tooltip="Coût de réponse à un appel d'offres (jours-homme × coût journalier). S'ajoute au coût de l'équipe : à réserver à la charge externe ou marginale par dossier."
            >
              <NumberInput
                id="presalesCostPerDeal"
                value={presalesCostPerDeal}
                onChange={(v) => updateAcquisition({ presalesCostPerDeal: v })}
                min={0}
                decimals={0}
              />
            </Field>
            <p className="text-xs text-muted-foreground">
              Chaque signature représente 1 / taux de win dossiers traités, facturés au coût
              ci-dessus le mois de la signature.
            </p>
          </>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Le rythme d’acquisition est réparti chaque mois entre les paliers selon les parts
        d&apos;acquisition définies.
      </p>

      {headcountRampEnabled && (
        <p className="text-xs text-muted-foreground">
          Si la montée en charge de l&apos;effectif est activée dans le panneau « Coûts », sa courbe
          suit ce modèle et cette durée.
        </p>
      )}

      <div className="rounded-md border p-3 text-sm">
        <p className="mb-1 font-medium">Objectif ROI sur l&apos;horizon</p>
        {required === null ? (
          <p className="text-muted-foreground">
            Non atteignable sur cet horizon, quelles que soient les acquisitions.
          </p>
        ) : roiReached ? (
          <p className="text-green-500">
            ✓ ROI atteint — rythme de croisière minimum : {formatNumber(required)} clients/mois
          </p>
        ) : (
          <p className="text-destructive">
            {formatNumber(required)} clients/mois de croisière nécessaires (actuellement{' '}
            {formatNumber(current, Number.isInteger(current) ? 0 : 2)})
          </p>
        )}
      </div>
    </div>
  );
}
